import {Assert} from "js-vextensions";
import {AssertUnreachable} from "web-vcore";

const maxUint32 = 4294967295;
const maxUint32_plus1 = maxUint32 + 1;
export class SeedInfo {
	/** Number between 0[inclusive] and 1[exclusive]. Of `seed_float` and `seed_int`, only one needs to be provided. */
	seed_float?: number;
	/** Number between 0[inclusive] and max-uint32[4294967295, exclusive]. Of `seed_float` and `seed_int`, only one needs to be provided. */
	seed_int?: number;
	initState? = true;
}

export class GetNextIntOptions {
	/**
	* fpMultiply is generally recommended, as it means bias in the lowest-bits (which some PRNGs produce) are less impactful.
	* (fpMultiply "makes slices" across the high-level FP range, whereas modulus reads exactly from those lowest, most biased bits)
	*/
	approach = "fpMultiply" as "fpMultiply" | "modulus";
	/**
	* If integer result came from the "biased segment", recall the base PRNG to obtain a new result.
	* Pro: Removes the (very small) mapping bias between source-range and integer-results.
	* Con: Changing a call's range may recall the base PRNG, changing the result of the next call.
	*/
	ensureUnbiased = false;
}

export abstract class PRNG {
	constructor(seedInfo: SeedInfo) {
		this.SetSeed(seedInfo);
	}
	seed_float: number;
	seed_int: number;
	SetSeed(seedInfo: SeedInfo) {
		seedInfo = {...new SeedInfo(), ...seedInfo};
		if (seedInfo.seed_float == null && seedInfo.seed_int == null) throw new Error("You must provide either seed_float or seed_int.");

		this.seed_float = seedInfo.seed_float ?? (seedInfo.seed_int! / maxUint32_plus1);
		this.seed_int = seedInfo.seed_int ?? Math.floor(seedInfo.seed_float! * maxUint32_plus1);
		if (seedInfo.initState) this.InitState();
	}

	abstract InitState();
	/** Returns an int between 0[inclusive] and max-uint32[4294967295, inclusive]. (This is the raw output of the prngs.)*/
	abstract GetNextUint32(): number;
	/** Returns a float between 0[inclusive] and 1[exclusive] -- like Math.random(). */
	GetNextFloat() {
		return this.GetNextUint32() / 4294967296;
	}
	/** Returns an int between min[inclusive] and max[inclusive]. */
	GetNextInt(min_incl: number, max_incl: number, opt?: GetNextIntOptions) {
		opt = {...new GetNextIntOptions(), ...opt};

		// we use flooring, meaning each possible int-result maps from the float-values between X and X+1; thus range is [dist between min and max] + 1
		// (0 maps from 0.0-1.0, 50 maps from 50.0-51.0, 200 maps from 200.0-201.00, etc.)
		const range = (max_incl - min_incl) + 1;

		// For list of approaches, see here: https://www.pcg-random.org/posts/bounded-rands.html
		// ==========

		// We have this the default (rather than modulus) because:
		// 1) Bias in the lowest-bits (which some PRNGs produce) are less impactful; this approach "makes slices" across the high-level FP range, whereas modulus reads exactly from those lowest, most biased bits.
		if (opt.approach == "fpMultiply") {
			if (opt.ensureUnbiased) throw new Error(`Cannot use ensureUnbaised flag with approach "fpMultiply".`);
			return min_incl + Math.floor(this.GetNextFloat() * range);
		}

		// from: https://pthree.org/2018/06/13/why-the-multiply-and-floor-rng-method-is-biased
		if (opt.approach == "modulus") {
			// round down max-uint32 to the first multiple of `range`, to find the start of the "biased segment"
			// (the "biased segment" is the final set of float-values in the 2**32 range, which starts at the last multiple of `range`, and in which not every integer has a float-value that maps to it)
			const startOfBiasedSegment = Math.floor((2 ** 32) / range) * range;
			do {
				//var x = Math.floor(this.GetNextFloat() * (2 ** 32)); // pick a number between 0[incl] and 2^32[excl].
				var x = this.GetNextUint32(); // pick a number between 0[incl] and 2^32[excl].
			} while (opt.ensureUnbiased && x >= startOfBiasedSegment); // try again if x is in the "biased segment"
			return min_incl + (x % range);
		}
		AssertUnreachable(opt.approach);
	}
}